import { useMemo } from 'react';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ChevronLeft, ChevronRight, MoreVertical, Edit2, Trash2, FileText, Camera, Layers } from 'lucide-react';

function OrderRow({ project, onCardClick, onEdit, onDelete, onMoveStage }) {
  const stages = project.stages || [];
  const currentIndex = stages.findIndex(s => s.id === project.current_stage_id);
  const currentStage = stages[currentIndex];
  const canPrev = currentIndex > 0;
  const canNext = currentIndex >= 0 && currentIndex < stages.length - 1;
  const hasNotes = Boolean(project.notes && project.notes.trim());
  const photoCount = project.photos?.length || 0;

  const stop = (e) => e.stopPropagation();

  return (
    <div
      className="group flex items-center gap-3 bg-[#2C3038] hover:bg-[#33383F] border border-[#33383F] rounded-xl px-3 py-2.5 cursor-pointer transition-colors"
      onClick={() => onCardClick(project)}
      data-testid={`order-row-${project.id}`}>

      {/* Left: name + client */}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-medium truncate">{project.client_name}</p>
        <h4 className="text-sm font-semibold text-[#F0EDE8] truncate">{project.title}</h4>
      </div>

      {/* Middle: indicators */}
      <div className="hidden sm:flex items-center gap-2 text-[#555D68]">
        {hasNotes && <FileText className="w-3.5 h-3.5" title="Has notes" />}
        {photoCount > 0 && (
          <span className="flex items-center gap-0.5 text-[10px]">
            <Camera className="w-3.5 h-3.5" />{photoCount}
          </span>
        )}
        {stages.length > 0 && (
          <span className="flex items-center gap-0.5 text-[10px] font-mono">
            <Layers className="w-3.5 h-3.5" />{currentIndex >= 0 ? currentIndex + 1 : '?'}/{stages.length}
          </span>
        )}
      </div>

      {/* Stage badge */}
      {currentStage && (
        <div className="flex items-center gap-1.5 flex-shrink-0" onClick={stop} onPointerDown={stop} onMouseDown={stop}>
          <Button variant="ghost" size="icon"
            className="h-6 w-6 text-[#9CA3AF] hover:bg-[#1A1D21] rounded-lg disabled:opacity-30"
            disabled={!canPrev}
            onClick={() => onMoveStage(project, 'prev')}
            data-testid={`stage-prev-${project.id}`}>
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <Badge
            className="border-0 text-white text-xs px-2.5 py-1 rounded-lg font-semibold whitespace-nowrap min-w-[110px] justify-center"
            style={{ backgroundColor: currentStage.color }}
            data-testid={`current-stage-${project.id}`}>
            {currentStage.name}
          </Badge>
          <Button variant="ghost" size="icon"
            className="h-6 w-6 text-[#9CA3AF] hover:bg-[#1A1D21] rounded-lg disabled:opacity-30"
            disabled={!canNext}
            onClick={() => onMoveStage(project, 'next')}
            data-testid={`stage-next-${project.id}`}>
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {/* Menu */}
      <div onClick={stop} onPointerDown={stop} onMouseDown={stop}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon"
              className="h-7 w-7 hover:bg-[#1A1D21] rounded-lg"
              data-testid={`order-menu-${project.id}`}>
              <MoreVertical className="w-4 h-4 text-[#9CA3AF]" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#23272E] border-[#33383F]"
            onCloseAutoFocus={(e) => e.preventDefault()}>
            <DropdownMenuItem onSelect={() => onEdit(project)} data-testid={`order-edit-${project.id}`}>
              <Edit2 className="w-4 h-4 mr-2" />Edit
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onDelete(project.id)} className="text-[#DC2626] focus:text-[#DC2626]"
              data-testid={`order-delete-${project.id}`}>
              <Trash2 className="w-4 h-4 mr-2" />Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export function ActiveOrdersPanel({ projects, onCardClick, onEdit, onDelete, onMoveStage }) {
  const active = useMemo(
    () => projects.filter(p => p.status !== 'quote'),
    [projects]
  );

  return (
    <div className="bg-[#23272E] border border-[#33383F] rounded-2xl p-4 md:p-5 shadow-sm" data-testid="active-orders-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#4A9D6E]" />
          <h2 className="font-bold text-[#F0EDE8] tracking-tight text-lg">Active Orders</h2>
        </div>
        <Badge className="bg-[#4A9D6E]/15 text-[#4A9D6E] border-0 rounded-xl" data-testid="active-orders-count">
          {active.length}
        </Badge>
      </div>

      {active.length === 0 ? (
        <div className="text-center py-14 text-[#9CA3AF]">
          <p className="text-sm">No active orders yet</p>
          <p className="text-xs mt-1 text-[#555D68]">Click &quot;New Project&quot; to create one</p>
        </div>
      ) : (
        <ScrollArea className="max-h-[calc(100vh-460px)] min-h-[300px] pr-2">
          <div className="space-y-2">
            {active.map(project => (
              <OrderRow
                key={project.id}
                project={project}
                onCardClick={onCardClick}
                onEdit={onEdit}
                onDelete={onDelete}
                onMoveStage={onMoveStage}
              />
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
